/**
 * Maps the `org_suggested_times_fetch_concurrency` flag value to a concurrency limit:
 * positive integers cap in-flight tasks; anything else (0, unset, bad data) means unlimited.
 */
export function concurrencyLimitFromFlag(flagValue: number): number {
  return Number.isInteger(flagValue) && flagValue > 0 ? flagValue : Infinity;
}

/**
 * Creates a FIFO semaphore that runs no more than `limit` tasks at once.
 */
export function createConcurrencyLimiter(limit: number) {
  if (limit !== Infinity && (!Number.isInteger(limit) || limit <= 0)) {
    throw new RangeError('Concurrency limit must be a positive integer or Infinity.');
  }

  let runningTaskCount = 0;
  const queuedTasks: Array<() => void> = [];

  const startQueuedTasks = (): void => {
    while (runningTaskCount < limit && queuedTasks.length > 0) {
      queuedTasks.shift()?.();
    }
  };

  return {
    run<T>(task: () => Promise<T>): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        queuedTasks.push(() => {
          runningTaskCount += 1;

          void (async () => {
            try {
              resolve(await task());
            } catch (error) {
              reject(error);
            } finally {
              runningTaskCount -= 1;
              startQueuedTasks();
            }
          })();
        });

        startQueuedTasks();
      });
    },
  };
}

/**
 * Runs promise-returning tasks with no more than `limit` tasks in flight at once.
 */
export async function runWithConcurrencyLimit<T>(tasks: Array<() => Promise<T>>, limit: number): Promise<T[]> {
  const limiter = createConcurrencyLimiter(limit);
  const results: T[] = [];
  results.length = tasks.length;
  let firstError: unknown;
  let hasError = false;

  await Promise.all(
    tasks.map(async (task, index) => {
      try {
        results[index] = await limiter.run(task);
      } catch (error) {
        hasError = true;
        firstError ??= error;
      }
    })
  );

  if (hasError) {
    throw firstError;
  }

  return results;
}
